import { useEffect, useMemo } from 'react';
import { useLocale } from '../../store/i18nMessage';
import { seoStateStore } from '../../store/seo';
import { DEFAULT_LOCALE, LOCALES, replacePath } from '@web3/utils';
import { isBrowser } from '../../utils/trick';
import { createPortal } from 'react-dom';
import logoUrl from './images/logo.png';

export type Web3SeoHeadProps = {
  renderHead?: boolean;
};

const hasTitle = Boolean(isBrowser && document.querySelector('title'));

export function Web3SeoHead({ renderHead = false }: Web3SeoHeadProps) {
  const seoInfo = useSeo();

  useEffect(() => {
    if (!isBrowser) {
      return;
    }
    const htmlEle = document.querySelector('html');
    if (htmlEle) {
      Object.entries(seoInfo.htmlAttrs).forEach(([attr, value]) => {
        htmlEle.setAttribute(attr, value);
      });
    }
    document.title = seoInfo.title;
  }, [seoInfo]);

  const headEle = document.querySelector('head');

  if (!headEle || !renderHead) {
    return;
  }

  return (
    <>
      {createPortal(
        <>
          {!hasTitle && <title>{seoInfo.title}</title>}
          {seoInfo.meta.map((meta, idx) => {
            return <meta key={[meta.name || meta.property, idx].join('-')} content={meta.content} name={meta.name} property={meta.property} />;
          })}
          {seoInfo.link.map((link, idx) => {
            return <link key={[link.href, idx].join('-')} hrefLang={link.hreflang} href={link.href} rel={link.rel} />;
          })}
        </>,
        headEle,
      )}
    </>
  );
}

const useSeo = () => {
  const { t, locale } = useLocale();
  const { lastSeoSetting, currentUrl } = seoStateStore.useStoreState();
  const { origin = '', pathname = '', search = '' } = currentUrl || {};
  let { href = '' } = currentUrl || {};
  const { appSeoConfigs, defaultMetaConfig } = getConfig(t);
  const seoMeta = useMemo(() => {
    const urlPath = pathname.replace(`/${locale}/web3`, '');
    const appConfig = appSeoConfigs.find(({ pattern }) => pattern.test(urlPath))?.config;
    return { ...defaultMetaConfig, appConfig, ...lastSeoSetting };
  }, [locale, pathname, appSeoConfigs, defaultMetaConfig, lastSeoSetting]);

  const swapSuffixRegex = /\/swap\/?$/;
  if (swapSuffixRegex.test(href)) {
    href = href.replace(/\/?$/, '/bridge');
  }
  const ogTitle = seoMeta.mediaShare?.og?.title || seoMeta.title;
  const ogDescription = seoMeta.mediaShare?.og?.desc || seoMeta.desc;
  const twitterTitle = seoMeta.mediaShare?.twitter?.title || ogTitle;
  const twitterDescription = seoMeta.mediaShare?.twitter?.desc || ogDescription;

  const tmp = useMemo(
    () => ({
      htmlAttrs: { lang: transformationLang(locale) },
      title: seoMeta.title,
      meta: [
        { name: 'keywords', content: seoMeta.keywords },
        { name: 'description', content: seoMeta.desc },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: ogTitle },
        { property: 'og:description', content: ogDescription },
        { property: 'og:url', content: origin },
        { property: 'og:image', content: logoUrl },
        { property: 'twitter:title', content: twitterTitle },
        { property: 'twitter:description', content: twitterDescription },
        { property: 'twitter:image', content: logoUrl },
        { property: 'twitter:card', content: 'summary_large_image' },
      ],
      link: [
        { rel: 'canonical', href },
        { rel: 'alternate', hreflang: 'x-default', href: origin + replacePath(pathname, DEFAULT_LOCALE) + search },
        ...LOCALES.map((item) => ({
          rel: 'alternate',
          hreflang: transformationLang(item.value),
          href: origin + replacePath(pathname, item.value) + search,
        })),
      ],
    }),
    [href, seoMeta, locale, origin, search, t, pathname],
  );
  return tmp;
};

const transformationLang = (lang = '') => {
  if (lang === 'es-419') {
    return 'es';
  }
  if (lang === 'fil-PH') {
    return 'tl';
  }
  return lang;
};

const getConfig = (t: (k: string) => string) => {
  const appSeoConfigs = [
    {
      pattern: /^\/inscription$/,
      config: {
        title: t('inscriptionSeoTitle'),
        desc: t('inscriptionSeoDesc'),
      },
    },
    {
      pattern: /^\/nft-pro$/,
      config: {
        title: t('nftSeoTitle'),
        desc: t('nftSeoDesc'),
      },
    },
    {
      pattern: /^\/airdrop$/,
      config: {
        title: t('rewardsSeoTitle'),
        desc: t('rewardsSeoDesc'),
      },
    },
    {
      pattern: /^\/crypto-ark$/,
      config: {
        title: t('arkSeoTitle'),
        desc: t('arkSeoDesc'),
      },
    },
    {
      pattern: /^\/ido$/,
      config: {
        title: t('idoSeoTitle'),
        desc: t('idoSeoDesc'),
      },
    },
    {
      pattern: /^\/swap\/market(\/.*)?$/,
      config: {
        title: 'Bybit Web3 Marketplace | View all Web3 DeFi Swap Pairs',
        desc: "Join Bybit's Web3 swapping platform to swap DeFi coins. Explore our huge range of DeFi Swapping pairs today.",
      },
    },
    {
      pattern: /^\/swap\/bridge(\/.*)?$/,
      config: {
        title: t('swapSeoTitle'),
        desc: t('swapSeoDesc'),
      },
    },
    {
      pattern: /^\/staking$/,
      config: {
        title: t('stakingSeoTitle'),
        desc: t('stakingSeoDesc'),
      },
    },
    {
      pattern: /^\/dex(\/.*)?$/,
      config: {
        title: 'Hot Token Real-time chart | On-Chain DeFi Swap | Bybit Web3 DEX Pro',
        desc: "Swap hot tokens securely with Bybit's Web3 DEX Pro. View live on-chain swap data on Bybit's Web3 DeFi DEX Pro",
      },
    },
  ];

  const defaultMetaConfig = {
    title: t('seoTitle'),
    desc: t('seoDesc'),
  };

  return {
    appSeoConfigs,
    defaultMetaConfig,
  };
};
